(() => {
  if (customElements.get('cro-product-video')) return;
  let active;
  const visibleGroups = new Set();
  const updateCartVisibility = () => document.documentElement.toggleAttribute('data-cro-video-visible', visibleGroups.size > 0);
  class ProductVideo extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.handledEscape = false;
      this.trigger = this.querySelector('[data-play]');
      this.stage = this.querySelector('[data-stage]');
      this.stopButton = this.querySelector('[data-stop]');
      this.trigger?.setAttribute('role', 'button');
      this.trigger?.addEventListener('keydown', event => {if (event.key === ' ') {event.preventDefault();this.play();}}, {signal:this.controller.signal});
      this.stopButton?.addEventListener('click', () => {this.stop();this.trigger.focus();}, {signal:this.controller.signal});
      this.addEventListener('keydown', event => {if (event.key === 'Escape' && this.hasAttribute('data-playing')) {event.preventDefault();event.stopPropagation();this.handledEscape = true;this.stop();this.trigger.focus();}}, {signal:this.controller.signal});
      this.addEventListener('keyup', event => {if (event.key === 'Escape' && this.handledEscape) {event.stopPropagation();this.handledEscape = false;}}, {signal:this.controller.signal});
      this.trigger?.addEventListener('click', event => {
        event.preventDefault();
        this.play();
      }, {signal: this.controller.signal});
    }
    play() {
      if (this.stage.firstElementChild) return;
      active?.stop();
      active = this;
      const content = this.querySelector('template')?.content.cloneNode(true);
      if (!content) return;
      const frame = content.querySelector('iframe');
      if (frame) {
        const url = new URL(frame.src);
        if (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com') url.hostname = 'www.youtube-nocookie.com';
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('playsinline', '1');
        frame.src = url.href;
        frame.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
        frame.setAttribute('tabindex', '0');
        frame.setAttribute('loading', 'eager');
      }
      const error = this.querySelector('[data-error]');
      if (error) error.hidden = true;
      this.stage.append(content);
      this.trigger.hidden = true;
      if (this.stopButton) this.stopButton.hidden = false;
      this.setAttribute('data-playing', '');
      const video = this.stage.querySelector('video');
      if (video) {
        this.mediaController = new AbortController();
        video.addEventListener('error', () => this.stop(true), {signal: this.mediaController.signal});
        video.addEventListener('play', () => {if (active !== this) {active?.stop(); active = this;}}, {signal: this.mediaController.signal});
        const sources = [...video.querySelectorAll('source')];
        const failedSources = new Set();
        sources.forEach(source => source.addEventListener('error', () => {
          failedSources.add(source);
          if (failedSources.size === sources.length && this.stage.contains(video)) this.stop(true);
        }, {signal: this.mediaController.signal}));
        video.play()?.catch(error => {
          // Permission failures keep native controls; failed media returns to the retry poster.
          if (error.name !== 'NotAllowedError' && error.name !== 'AbortError' && this.stage.contains(video)) this.stop(true);
        });
        video.focus();
      } else frame?.focus();
    }
    stop(failed = false) {
      const restoreFocus = failed && this.stage?.contains(document.activeElement);
      const video = this.stage?.querySelector('video');
      this.mediaController?.abort();
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      }
      this.stage?.replaceChildren();
      if (this.trigger) this.trigger.hidden = false;
      this.removeAttribute('data-playing');
      if (this.stopButton) this.stopButton.hidden = true;
      const error = this.querySelector('[data-error]');
      if (error) error.hidden = !failed;
      if (active === this) active = null;
      if (restoreFocus) this.trigger?.focus();
    }
    disconnectedCallback() {this.stop(); this.controller?.abort();}
  }
  customElements.define('cro-product-video', ProductVideo);
  class VideoGroup extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.videos = [...this.querySelectorAll('cro-product-video')];
      this.rail = this.querySelector('[data-videos]');
      const playlist = this.querySelector('[data-playlist]');
      if (playlist && !playlist.children.length && this.videos.length > 1) {
        this.videos.forEach((video, index) => {
          const button = document.createElement('button');
          button.type = 'button'; button.dataset.select = index;
          const image = video.querySelector('[data-play] img')?.cloneNode();
          if (image) {image.alt = '';button.append(image);}
          const title = document.createElement('span');
          title.textContent = video.querySelector('h3')?.textContent || String(index + 1);
          button.append(title);playlist.append(button);
        });
      }
      this.buttons = [...this.querySelectorAll('[data-select]')];
      const more = this.querySelector('[data-more]');
      if (more) {
        let expanded = false;
        const updateList = () => {
          this.buttons.forEach((button, index) => {button.hidden = !expanded && index >= 6;});
          more.hidden = this.buttons.length <= 6;
          more.setAttribute('aria-expanded', String(expanded));
          more.textContent = expanded ? more.dataset.lessLabel : more.dataset.moreLabel;
        };
        updateList();
        more.addEventListener('click', () => {
          expanded = !expanded;
          if (!expanded && this.buttons.some((button, i) => i >= 6 && button.getAttribute('aria-pressed') === 'true')) this.select(0);
          updateList();
        }, {signal});
      }
      if (this.dataset.layout === 'playlist') {
        this.buttons.forEach((button, index) => button.addEventListener('click', () => this.select(index), {signal}));
        this.select(0);
      }
      this.querySelectorAll('[data-direction]').forEach(button => button.addEventListener('click', () => {
        const card = this.videos[0];
        this.rail.scrollBy({left: Number(button.dataset.direction) * ((card?.getBoundingClientRect().width || 240) + 20), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }, {signal}));
      const update = () => {
        const arrows = this.querySelector('.cro-videos__arrows');
        if (arrows) arrows.hidden = this.rail.scrollWidth <= this.rail.clientWidth + 1;
        const prev = this.querySelector('[data-direction="-1"]');
        const next = this.querySelector('[data-direction="1"]');
        const railStyle = getComputedStyle(this.rail);
        const startInset = parseFloat(railStyle.paddingLeft) || 0;
        const endInset = parseFloat(railStyle.paddingRight) || 0;
        if (prev) prev.disabled = this.rail.scrollLeft <= startInset + 1;
        if (next) next.disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - endInset - 1;
      };
      this.rail?.addEventListener('scroll', update, {signal, passive:true});
      if (window.ResizeObserver && this.rail) {this.observer = new ResizeObserver(update);this.observer.observe(this.rail);}
      update();
      this.visibilityObserver?.disconnect();
      if (window.IntersectionObserver) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => entry.isIntersecting ? visibleGroups.add(this) : visibleGroups.delete(this));
          updateCartVisibility();
        });
        this.visibilityObserver.observe(this);
      }
      this.setAttribute('data-enhanced', '');
    }
    select(index) {
      this.videos.forEach((video, i) => {video.stop();video.hidden = i !== index;});
      this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    }
    disconnectedCallback() {this.controller?.abort();this.observer?.disconnect();this.visibilityObserver?.disconnect();visibleGroups.delete(this);updateCartVisibility();this.videos?.forEach(video => video.stop());}
  }
  customElements.define('cro-video-group', VideoGroup);
})();
